import { getTheme } from 'themes'
import { toCamelCase } from 'utils/stringHelpers'
import { deepMerge } from 'grommet/utils'
import { isFlagOn } from 'utils/featureFlags'
import { isPreviewMode } from 'utils/highschoolHelpers'
import { routeProductMap } from '../product/configs'
import {
  RouteContentProductAppMap,
  AppToProductSegments,
} from './routeProductMaps'

const USER_CONTEXT_OVERRIDE_KEY = 'userContextOverride'

export function getPathname(location = window.location) {
  if (location?.pathname) {
    return location.pathname
  }

  if (location?.href) {
    try {
      return new URL(location.href).pathname
    } catch {
      return ''
    }
  }

  return ''
}

export function getHref(location = window.location) {
  return location?.href || ''
}

// #region Shared path matchers

export const matchesHighSchoolPath = pathname =>
  pathname.includes('/product/high-school') ||
  pathname.includes('/high-school') ||
  pathname.includes('/dashboard/grades-9-12')

export const matchesMiddleSchoolTier2Path = pathname =>
  pathname.includes('/product/middle-school/lessons/tier-2') ||
  pathname.includes('/middle-school/lessons/tier-2') ||
  pathname.includes('/dashboard/grades-6-8/tier-2')

export const matchesMiddleSchoolPath = pathname =>
  pathname.includes('/product/middle-school') ||
  pathname.includes('/middle-school') ||
  pathname.includes('/dashboard/grades-6-8')

export const matchesElementaryTier2Path = pathname =>
  pathname.includes('/product/elementary/lessons/tier-2') ||
  pathname.includes('/elementary/lessons/tier-2') ||
  pathname.includes('/dashboard/grades-k-5/tier-2')

export const matchesElementaryPath = pathname =>
  pathname.includes('/product/elementary') ||
  pathname.includes('/elementary') ||
  pathname.includes('/dashboard/grades-k-5')

export const matchesElementaryProductPath = pathname =>
  pathname.includes('/product/elementary') ||
  getDigitalWellBeingProgramType(pathname) === 'k5'

export const matchesMiddleSchoolProductPath = pathname =>
  pathname.includes('/product/middle-school') ||
  getDigitalWellBeingProgramType(pathname) === 'middleSchool'

export const matchesHighSchoolProductPath = pathname =>
  pathname.includes('/product/high-school') ||
  getDigitalWellBeingProgramType(pathname) === 'highSchool'

// #endregion

// #region Digital Well-Being route helpers

export const isDigitalWellBeingPath = (pathname = '') =>
  pathname.includes('/digital-well-being')

export const isDigitalWellBeingUrl = () => isDigitalWellBeingPath(getPathname())

const resolveDigitalWellBeingRouteServiceApp = () =>
  RouteContentProductAppMap.learnDwbProduct

export const getDigitalWellBeingAudienceSegment = (pathname = '') => {
  const audienceSegmentMatch = pathname.match(
    /^\/(?:product\/)?digital-well-being(?:\/lessons(?:\/(?:preview|teach)\/([^/?#]+))?)?(?:[/?#]|$)/,
  )
  return audienceSegmentMatch?.[1]?.toLowerCase() || null
}

export function getDigitalWellBeingProgramType(pathname = '') {
  const audienceSegment = getDigitalWellBeingAudienceSegment(pathname)
  if (!audienceSegment) {
    return null
  }

  if (audienceSegment === 'training') return 'middleSchool'
  if (audienceSegment.startsWith('grade-k')) return 'k5'

  const gradeNumberMatch = audienceSegment.match(/^grade-(\d+)/)
  if (!gradeNumberMatch) return 'middleSchool'

  const gradeNumber = parseInt(gradeNumberMatch[1], 10)
  if (gradeNumber >= 0 && gradeNumber <= 5) return 'k5'
  if (gradeNumber >= 6 && gradeNumber <= 8) return 'middleSchool'
  if (gradeNumber >= 9 && gradeNumber <= 12) return 'highSchool'
  return 'middleSchool'
}

/**
 * True when a URL should fetch from curriculum-service (v3) rather than
 * route-service (v2). Only K12 product trees: digital well-being,
 * elementary, middle-school, and high-school. Reuses the same product-path
 * matchers as isK5Url / isMiddleSchoolUrl / isHighSchoolUrl.
 *
 * Mind Yeti, family resources, OST, SELA, and other products stay on v2.
 * Contentful (v1) PagesContainer routes are not gated here.
 */
export function isCurriculumServiceProductPath(pathname = '') {
  return (
    isDigitalWellBeingPath(pathname) ||
    matchesElementaryProductPath(pathname) ||
    matchesMiddleSchoolProductPath(pathname) ||
    matchesHighSchoolProductPath(pathname)
  )
}

// #endregion

// #region Preview / tier-2 route helpers

export const isTier2Url = () => getPathname().includes('/tier-2')

export const isTier2PreviewMode = () =>
  isTier2Url() &&
  getHref()
    .toLowerCase()
    .includes('preview')

export const isPreviewRoute = () =>
  getPathname().includes('/preview/') || isTier2PreviewMode()

export const isDigitalWellBeingPreviewMode = () =>
  isDigitalWellBeingUrl() && isPreviewRoute()

/**
 * Whether a curriculum unit/lesson page is in preview (not teach) mode.
 *
 * Used for preview banners and hiding completion UI across UnitV3,
 * TieredInterventions, and related v3 pages. Honors the route verb first,
 * then URL path segments, then shared route helpers that read window.location.
 *
 * @param {Object} [params]
 * @param {Object} [params.match] - react-router match
 * @param {Object} [params.location] - react-router location
 * @returns {boolean}
 */
export function resolveCurriculumPreviewMode({ match, location } = {}) {
  const routeVerb = String(match?.params?.verb || '').toLowerCase()
  if (routeVerb === 'preview') {
    return true
  }

  const pathCandidates = [match?.url, location?.pathname, getPathname()].filter(
    Boolean,
  )

  if (pathCandidates.some(path => String(path).includes('/preview/'))) {
    return true
  }

  return (
    isPreviewRoute() || isTier2PreviewMode() || isDigitalWellBeingPreviewMode()
  )
}

/** Whether a curriculum unit/lesson page is in teach (not preview) mode */
export function resolveCurriculumTeachMode({ match, location } = {}) {
  const routeVerb = String(match?.params?.verb || '').toLowerCase()
  if (routeVerb === 'teach') {
    return true
  }

  if (routeVerb === 'preview') {
    return false
  }

  const pathCandidates = [match?.url, location?.pathname, getPathname()].filter(
    Boolean,
  )

  return pathCandidates.some(path => String(path).includes('/teach/'))
}

export const DWB_PROGRAM_SUPPORTS_BASE_PATH =
  '/product/digital-well-being/program-supports'

export const DWB_PROGRAM_SUPPORTS_ARTICLE = `${DWB_PROGRAM_SUPPORTS_BASE_PATH}/:articleSlug`

export function resolveDwbProgramSupportsArticleSlug(routeValue = '') {
  const normalized = String(routeValue || '').trim()
  if (!normalized) return ''

  if (normalized.startsWith(`${DWB_PROGRAM_SUPPORTS_BASE_PATH}/`)) {
    return normalized.slice(DWB_PROGRAM_SUPPORTS_BASE_PATH.length + 1)
  }

  if (normalized.startsWith('/program-supports/')) {
    return normalized.replace(/^\/program-supports\//, '')
  }

  if (normalized.startsWith('/digital-well-being/program-supports/')) {
    return normalized.replace(/^\/digital-well-being\/program-supports\//, '')
  }

  return normalized.replace(/^\//, '')
}

export function buildDwbProgramSupportsBrowserPath(articleSlug = '') {
  const slug = resolveDwbProgramSupportsArticleSlug(articleSlug)
  return slug ? `${DWB_PROGRAM_SUPPORTS_BASE_PATH}/${slug}` : ''
}

export function buildDwbProgramSupportsCurriculumRoutePath(articleSlug = '') {
  const slug = resolveDwbProgramSupportsArticleSlug(articleSlug)
  return slug ? `/program-supports/${slug}` : '/program-supports'
}

const TRAINING_ARTICLE_PATH_PATTERN = /^\/product\/[^/]+\/(trainings|training)\/[^/]+/

const TRAINING_LANDING_PATH_PATTERN = /^\/product\/[^/]+\/(trainings|training)$/

/**
 * Returns true when pathname matches a training article route
 * (e.g. /product/elementary/trainings/intro or /product/high-school/training/foo).
 */
export function isTrainingArticleRoute(pathname = getPathname()) {
  return TRAINING_ARTICLE_PATH_PATTERN.test(pathname)
}

/**
 * Returns true when pathname matches a training landing route
 * (e.g. /product/elementary/trainings or /product/high-school/training).
 */
export function isTrainingLandingRoute(pathname = getPathname()) {
  return TRAINING_LANDING_PATH_PATTERN.test(pathname)
}

// #endregion

/**
 * Extracts the product slug from a `/product/:productName/...` URL path.
 * @param {string} pathname
 * @returns {string|undefined}
 */
export function getProductNameFromUrl(pathname) {
  if (typeof pathname !== 'string' || !pathname.trim()) return undefined
  if (!pathname.startsWith('/product/')) return undefined

  // pathname is "/product/<slug>/..." → ['', 'product', '<slug>', ...]
  const productName = pathname.split('/')[2]
  return productName || undefined
}

export const getProductFromUrl = () => {
  const pathname = getPathname()
  let productName

  const isProductPath = pathname.includes('/product/')
  const isReportsPath = pathname.includes('/reports/')
  const isDistrictPath = pathname.includes('/district/')
  const isHighSchoolFamilyResourcesPath = pathname.includes('/family/')
  const isFamilyResourcesPath = pathname.includes('/family-resources/')
  const isMindYetiFamilyPath = pathname.includes('/family-mind-yeti/')
  const isAdminPath = pathname.includes('/admin')
  const isDashboardPath = pathname.includes('/dashboard')
  const isOstKitPathK1 = pathname.includes('/out-of-school-time/grades-k-1')
  const isOstKitPath23 = pathname.includes('/out-of-school-time/grades-2-3')
  const isOstKitPath45 = pathname.includes('/out-of-school-time/grades-4-5')
  const isOstKitDefaultPath = pathname.includes('/out-of-school-time')
  const notOstPath =
    !isOstKitDefaultPath &&
    !isOstKitPathK1 &&
    !isOstKitPath23 &&
    !isOstKitPath45

  if (isAdminPath) productName = 'admin'
  if (isDashboardPath) {
    productName = 'dashboard'
  }
  if (isDistrictPath) productName = 'district'
  if (isFamilyResourcesPath) productName = 'familyResources'
  if (isHighSchoolFamilyResourcesPath) productName = 'familyResourcesHS'
  if (isMindYetiFamilyPath) productName = 'mind-yeti'
  if (isReportsPath) productName = 'reports'
  if (isOstKitPathK1) {
    productName = 'outOfSchoolTimeK1'
  } else if (isOstKitPath23) {
    productName = 'outOfSchoolTime23'
  } else if (isOstKitPath45) {
    productName = 'outOfSchoolTime45'
  } else if (isOstKitDefaultPath) {
    productName = 'outOfSchoolTime'
  }
  if (isProductPath && notOstPath) {
    productName = getProductNameFromUrl(pathname)
  }
  if (!productName) {
    return undefined
  }

  return routeProductMap[toCamelCase(productName)]
}

export const getThemeFromUrl = () => {
  let mainTheme = getTheme('main')
  const platformTheme = getTheme('platform')
  const pathname = getPathname()

  const isAccessibility = pathname.includes('/accessibility')
  const isWebinars = pathname.includes('/webinars')
  const isDashboardPath = pathname.includes('/dashboard')
  if (isDashboardPath || isAccessibility || isWebinars)
    return deepMerge(mainTheme, platformTheme)

  if (isDigitalWellBeingUrl()) {
    const productTheme = getTheme('middleSchool')
    return deepMerge(mainTheme, productTheme)
  }

  const product = getProductFromUrl()
  if (product) {
    const productTheme = getTheme(product?.themeName)
    mainTheme = deepMerge(mainTheme, productTheme)
  }

  return mainTheme
}

export const isMiddleSchoolProductJuneRelease = (_, subscribedFlags) => {
  const pathname = getPathname()
  return (
    subscribedFlags['middle-school-june-release'] &&
    pathname.includes('product/middle-school')
  )
}

export const isHighSchoolOrMiddleSchoolJuneRelease = (_, subscribedFlags) => {
  const pathname = getPathname()
  return (
    (subscribedFlags['high-school-june-release'] &&
      pathname.includes('product/high-school')) ||
    isMiddleSchoolProductJuneRelease(_, subscribedFlags)
  )
}

export const isNewFamilyResources = () =>
  getPathname().startsWith('/family-resources')

export function isHighSchoolExtensionActivities(_, subscribedFlags) {
  const pathname = getPathname()

  return (
    subscribedFlags['feature_learn-18453-hs-student-extension-activities'] &&
    pathname.includes('product/high-school')
  )
}

export const isFamilyResourcesPageInProgramFlex = () => {
  const pathname = getPathname()
  return (
    pathname.startsWith('/family-resources') ||
    pathname.startsWith('/family/high-school')
  )
}

export const isMiddleSchoolProgramFlex = (_, subscribedFlags) => {
  return (
    isMiddleSchoolUrl() &&
    subscribedFlags['feature-learn-18843-ms-program-flex']
  )
}

export const isMsSiteSelection = (_, subscribedFlags) => {
  return (
    isMiddleSchoolUrl() &&
    subscribedFlags['feature_LEARN-20108-ms-site-selection']
  )
}

export const isK5SiteSelection = (_, subscribedFlags) => {
  return isK5Url() && subscribedFlags['feature_LEARN-20164-k5-site-selection']
}

export const isSiteSelectionV2 = (_, subscribedFlags) => {
  return (
    (isMiddleSchoolDashboard() &&
      subscribedFlags['feature_LEARN-20108-ms-site-selection']) ||
    (isK5Dashboard() &&
      subscribedFlags['feature_LEARN-20164-k5-site-selection'])
  )
}

export const showSiteSelector = (
  checkMap = { checkHs: true, checkMs: true, checkK5: true, checkDwb: false },
) => {
  let showMs = false
  let showK5 = false
  let showHs = false
  let showDwb = false

  if (checkMap.checkMs) {
    const isMiddleSchool = isMiddleSchoolUrl()
    const isMsSiteSelectionFlag = isFlagOn([
      'feature_LEARN-20108-ms-site-selection',
    ])
    showMs = isMiddleSchool && isMsSiteSelectionFlag
  }

  if (checkMap.checkK5) {
    showK5 = isK5Url()
  }

  if (checkMap.checkHs) {
    const isNOTPreviewHs = isHighSchoolUrl() && !isPreviewMode()
    showHs = isNOTPreviewHs
  }

  if (checkMap.checkDwb) {
    showDwb = isDigitalWellBeingUrl()
  }

  return showMs || showHs || showK5 || showDwb
}

export function extractUserContextOverride(location = window.location) {
  const searchParams = new URLSearchParams(location.search)
  const userContextOverride = searchParams.get(USER_CONTEXT_OVERRIDE_KEY)
  if (userContextOverride) {
    sessionStorage.setItem(USER_CONTEXT_OVERRIDE_KEY, userContextOverride)
  }
  const sessionStickyUserContextOverride = sessionStorage.getItem(
    USER_CONTEXT_OVERRIDE_KEY,
  )
  if (sessionStickyUserContextOverride) {
    return sessionStickyUserContextOverride
  }

  return userContextOverride
}

export const isHighSchoolFamilyResources = () =>
  getPathname().startsWith('/family/')

export function resolveRouteServiceApp(path) {
  if (path.startsWith('/dashboard/grades-9-12')) {
    return RouteContentProductAppMap.learnHsProduct
  }
  if (path.startsWith('/dashboard/grades-6-8')) {
    return RouteContentProductAppMap.learnMsProduct
  }
  if (path.startsWith('/dashboard/grades-k-5')) {
    return RouteContentProductAppMap.learnK5Product
  }
  if (path.startsWith('/family-resources')) {
    return RouteContentProductAppMap.learnFamilyResourcesK8
  }
  if (path.startsWith('/family')) {
    return RouteContentProductAppMap.learnFamilyResourcesHS
  }
  if (
    path.startsWith('/product/digital-well-being') ||
    path.startsWith('/digital-well-being')
  ) {
    return resolveDigitalWellBeingRouteServiceApp(path)
  }
  if (
    path.startsWith('/product/high-school') ||
    path.startsWith('/high-school')
  ) {
    return RouteContentProductAppMap.learnHsProduct
  }
  if (
    path.startsWith('/product/middle-school') ||
    path.startsWith('/middle-school')
  ) {
    return RouteContentProductAppMap.learnMsProduct
  }
  if (
    path.startsWith('/product/elementary') ||
    path.startsWith('/elementary')
  ) {
    return RouteContentProductAppMap.learnK5Product
  }
  throw new Error(`Path ${path} does not have a supported route-service app`)
}

export const isK5Url = () => matchesElementaryProductPath(getPathname())

export const isK5Dashboard = () =>
  getPathname().includes('/dashboard/grades-k-5')

export const isMiddleSchoolUrl = () =>
  matchesMiddleSchoolProductPath(getPathname())

export const isMiddleSchoolDashboard = () =>
  getPathname().includes('/dashboard/grades-6-8')

export const isHighSchoolUrl = () => matchesHighSchoolProductPath(getPathname())

export const isHighSchoolDashboard = () =>
  getPathname().includes('/dashboard/grades-9-12')

export const isK5DashboardOrK5Program = () => isK5Dashboard() || isK5Url()

export const isMiddleSchoolDashboardOrMiddleSchoolProgram = () =>
  isMiddleSchoolDashboard() || isMiddleSchoolUrl()

export const isHighSchoolDashboardOrHighSchoolProgram = () =>
  isHighSchoolDashboard() || isHighSchoolUrl()

export function stripProductAndProgramSegments(
  route,
  resolvedApp,
  appsToRemoveProductSegment,
) {
  if (appsToRemoveProductSegment.indexOf(resolvedApp) === -1) return route

  let path = route.replace('/product/', '/')

  const segments = AppToProductSegments[resolvedApp]
  if (segments) {
    for (const seg of segments) {
      const prefix = `/${seg}/`
      if (path.startsWith(prefix)) {
        path = '/' + path.slice(prefix.length)
        break
      }
      if (path === `/${seg}`) {
        path = '/'
        break
      }
    }
  }

  return path
}
